/**
 * Resolve static asset URLs for the IC assets canister.
 *
 * Local access modes (same canister, different routing):
 * - Official: http://{assetsId}.localhost:8000/  → absolute /assets/* works
 * - Legacy:   http://127.0.0.1:8000/?canisterId=  → absolute /assets/* needs ?canisterId=
 *
 * Prefer the subdomain form (see local-origin.ts redirect).
 */

const ASSETS_ID_RE =
	/^[a-z0-9]{5}-[a-z0-9]{5}-[a-z0-9]{5}-[a-z0-9]{5}-[a-z0-9]{3}$/i;

/** Assets canister id from URL, hostname, or ic_env cookie. */
export function resolveAssetsCanisterId(): string {
	if (typeof window === "undefined") return "";

	// 1) ?canisterId=
	try {
		const q = new URLSearchParams(window.location.search)
			.get("canisterId")
			?.trim();
		if (q) return q;
	} catch {
		/* ignore */
	}

	// 2) {id}.localhost
	const host = window.location.hostname.toLowerCase();
	if (host.endsWith(".localhost") && host !== "localhost") {
		const id = host.replace(/\.localhost\.?$/, "");
		if (id.includes("-")) return id;
	}

	// 3) ic_env cookie (asset canister discovery)
	try {
		const raw = document.cookie
			.split(";")
			.map((c) => c.trim())
			.find((c) => c.startsWith("ic_env="));
		if (raw) {
			const val = decodeURIComponent(raw.slice("ic_env=".length));
			// PUBLIC_CANISTER_ID:assets=<id>  (url-encoded variants)
			const m =
				val.match(/PUBLIC_CANISTER_ID:assets=([a-z0-9-]+)/i) ||
				val.match(/PUBLIC%5FCANISTER%5FID%3Aassets%3D([a-z0-9-]+)/i) ||
				val.match(/assets[=:]([a-z0-9]+-[a-z0-9-]+)/i);
			if (m?.[1]) return m[1];
		}
	} catch {
		/* ignore */
	}

	// 4) Vite bake
	try {
		const env = import.meta.env as Record<string, string | undefined>;
		const baked =
			env.VITE_CANISTER_ID_ASSETS?.trim() ||
			env.VITE_ASSETS_CANISTER_ID?.trim() ||
			"";
		if (baked) return baked;
	} catch {
		/* ignore */
	}

	return "";
}

export function isCanisterSubdomainHost(hostname?: string): boolean {
	const h = (hostname ?? (typeof window !== "undefined" ? window.location.hostname : "")).toLowerCase();
	if (!h.endsWith(".localhost") || h === "localhost") return false;
	const id = h.replace(/\.localhost\.?$/, "");
	return id.includes("-");
}

/**
 * Rewrite static path so it loads from the assets canister on local query-form hosts.
 * On {id}.localhost (and mainnet icp0.io) returns the path unchanged.
 */
export function publicUrl(path: string): string {
	const raw = (path ?? "").trim();
	if (!raw) return raw;
	if (/^(https?:|data:|blob:)/i.test(raw)) return raw;

	const normalized = raw.startsWith("/") ? raw : `/${raw}`;

	if (typeof window === "undefined") return normalized;

	// Subdomain / mainnet canister host: absolute paths already hit the assets canister
	if (isCanisterSubdomainHost()) return normalized;
	const h = window.location.hostname.toLowerCase();
	if (h.endsWith(".icp0.io") || h.endsWith(".ic0.app") || h.endsWith(".icp.net")) {
		return normalized;
	}

	// Query-form or bare 127.0.0.1/localhost → need canisterId
	const needsQuery =
		h === "127.0.0.1" ||
		h === "localhost" ||
		h === "[::1]" ||
		h === "0.0.0.0";

	if (!needsQuery) return normalized;

	const cid = resolveAssetsCanisterId();
	if (!cid) return normalized;

	try {
		const u = new URL(normalized, window.location.origin);
		if (!u.searchParams.has("canisterId")) {
			u.searchParams.set("canisterId", cid);
		}
		return `${u.pathname}${u.search}${u.hash}`;
	} catch {
		const sep = normalized.includes("?") ? "&" : "?";
		return `${normalized}${sep}canisterId=${encodeURIComponent(cid)}`;
	}
}

/** Set document favicon using publicUrl. */
export function applyDocumentIcons(opts?: {
	favicon?: string;
	appleTouch?: string;
}): void {
	if (typeof document === "undefined") return;
	const fav = publicUrl(opts?.favicon ?? "/favicon.ico");
	const apple = publicUrl(
		opts?.appleTouch ?? "/assets/motoko-dollar-logo.png",
	);

	let icon = document.querySelector<HTMLLinkElement>("link[rel='icon']");
	if (!icon) {
		icon = document.createElement("link");
		icon.rel = "icon";
		document.head.appendChild(icon);
	}
	icon.type = fav.includes(".png") ? "image/png" : "image/x-icon";
	icon.href = fav;

	// Second icon for png brand mark
	let iconPng = document.querySelector<HTMLLinkElement>(
		"link[rel='icon'][type='image/png']",
	);
	if (!iconPng) {
		iconPng = document.createElement("link");
		iconPng.rel = "icon";
		iconPng.type = "image/png";
		document.head.appendChild(iconPng);
	}
	iconPng.href = publicUrl("/assets/motoko-dollar-logo.png");

	let touch = document.querySelector<HTMLLinkElement>(
		"link[rel='apple-touch-icon']",
	);
	if (!touch) {
		touch = document.createElement("link");
		touch.rel = "apple-touch-icon";
		document.head.appendChild(touch);
	}
	touch.href = apple;
}

export function isAssetsIdShape(id: string): boolean {
	return ASSETS_ID_RE.test(id) || (id.includes("-") && id.endsWith("-cai"));
}
